import { FC, ReactNode, useContext } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from 'src/components/Footer';
import PaymentSuccessPage from '../../components/Payment/PaymentSuccessPage';
import PaymentFailedPage from '../../components/Payment/PaymentFailedPage';
import { useThemeMode } from '../../theme/ThemeProvider';
import { SidebarContext } from '../../contexts/SidebarContext';

interface LayoutProps {
  children?: ReactNode;
}

const Layout: FC<LayoutProps> = () => {
  const location = useLocation();
  const { isDark } = useThemeMode();
  const { isCollapsed } = useContext(SidebarContext);

  const isPaymentSuccess = location.pathname === '/payment-success';
  if (isPaymentSuccess) return <PaymentSuccessPage />;

  const isPaymentFailed = location.pathname === '/payment-failed';
  if (isPaymentFailed) return <PaymentFailedPage />;

  const footerRestrictedRoutes = [
    /^\/patients\/[^\/]+\/queries/,
    /^\/patient\/queries/,
    /^\/patient-details/,
  ];

  const shouldHideFooter = footerRestrictedRoutes.some(pattern => pattern.test(location.pathname));

  return (
    <div className="flex h-full overflow-x-hidden bg-surface-50 dark:bg-surface-dark transition-colors duration-500">

      {/* ── Premium ambient lighting system ── */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden select-none" aria-hidden="true">
        {/* Thin brand line at very top of viewport */}
        <div
          className={[
            'absolute left-0 right-0 h-[1px] top-0',
            isDark
              ? 'bg-gradient-to-r from-transparent via-dark-border-brand to-transparent'
              : 'bg-gradient-to-r from-transparent via-brand-500/[0.09] to-transparent',
          ].join(' ')}
        />
        {/* Primary brand radial — top-right, very subtle glow */}
        <div
          className={[
            'absolute w-[50vw] h-[50vw] max-w-[700px] max-h-[700px] rounded-full blur-[240px] -top-1/4 -right-[5%]',
            isDark ? 'opacity-[0.25]' : 'opacity-0',
          ].join(' ')}
          style={{ background: 'radial-gradient(circle, rgba(3,93,184,0.3) 0%, transparent 70%)' }}
        />
        {/* Secondary accent radial — bottom-left, warm tint */}
        <div
          className={[
            'absolute w-[40vw] h-[40vw] max-w-[600px] max-h-[600px] rounded-full blur-[200px] -bottom-[20%] -left-[8%] animate-[float_8s_ease-in-out_infinite_reverse]',
            isDark ? 'opacity-[0.18]' : 'opacity-0',
          ].join(' ')}
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.22) 0%, transparent 70%)' }}
        />
        {/* Tertiary warm accent — centre, very subtle */}
        <div
          className={[
            'absolute w-[25vw] h-[25vw] max-w-[400px] max-h-[400px] rounded-full blur-[180px] animate-glow-pulse top-[45%] left-[55%]',
            isDark ? 'opacity-[0.12]' : 'opacity-0',
          ].join(' ')}
          style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 70%)' }}
        />
        {/* Fine noise texture overlay for premium depth */}
        <div
          className="absolute inset-0 opacity-[0.012] dark:opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
          }}
        />
      </div>

      <Header />
      <Sidebar />

      {/* ── Main content area ── */}
      <main
        className={[
          'relative z-[1] flex-1 min-w-0 flex flex-col min-h-0 pt-[64px] transition-[margin-left] duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
          isCollapsed ? 'lg:ml-[72px]' : 'lg:ml-sidebar',
        ].join(' ')}
      >
        <div className="flex-1 overflow-y-auto overflow-x-hidden w-full">
          <Outlet />
        </div>
        {!shouldHideFooter && <Footer />}
      </main>
    </div>
  );
};

export default Layout;
